// Components==============
import updateUser from 'actions/authentication/updateUser';
import Button from 'components/UI/Button/Button';
import Graph from 'components/UI/Graph/Graph';
import ToggleButton from 'components/UI/ToggleButton/ToggleButton';
import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HomeContext } from 'screens/Home/Home';
import { generateKey } from 'utilities/generateKey';
import { GlobalContext } from 'utilities/GlobalContext';
import { usePrevious } from 'utilities/usePrevious';
import { MenuContext } from '../../Menu';
import Popup from '../Popup/Popup';
import './Calendar.module.scss';
import DatePicker from './Components/DatePicker/DatePicker';
import Days from './Components/Days/Days';
import { getInitialData } from './Components/initialData';
import LogBook from './Components/LogBook/LogBook';
import Password from './Components/Password/Password';
// =========================

export type CalendarContextTypes = {
    storeLogbook: () => void;
    canSave: boolean;
    setCanSave: React.Dispatch<React.SetStateAction<boolean>>;
    activeLog: LogBookEntry;
    setActiveLog: React.Dispatch<React.SetStateAction<LogBookEntry>>;
};

export const CalendarContext = createContext({} as CalendarContextTypes);

export default function Calendar() {
    const {
        select,
        selected,
        setMenuPointer,
        setActiveLogForDeleteModal,
    } = useContext(MenuContext);

    const { logbookAuthenticated } = useContext(GlobalContext);

    const [canSave, setCanSave] = useState(false);

    const dispatch = useDispatch();

    const logbook = useSelector(
        (state: ReduxStore) => state?.authentication?.user
    )?.logbook;

    const initialWeekGoal =
        useSelector(
            (state: ReduxStore) => state?.authentication?.user?.flash_goal
        ) || 0;

    const initialData = getInitialData(generateKey(10), initialWeekGoal);

    const filteredLogs = logbook?.filter((l) => !!l.attributes.firstDayOfWeek);
    const latestLog = filteredLogs?.[filteredLogs?.length - 1];
    const [activeLog, setActiveLog] = useState(latestLog || initialData);

    const [view, setView] = useState<'Jouw oefenweek' | 'Voortgang'>(
        'Jouw oefenweek'
    );

    const { setWelcomeY, welcomeSequence } = useContext(HomeContext);

    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (welcomeSequence === 'logbook') {
            setWelcomeY(`${ref?.current?.offsetTop}px`);
        }
    }, [welcomeSequence]);

    const graphData =
        useSelector((state: ReduxStore) =>
            state.graphData?.entities.allIds
                .map((id) => state.graphData?.entities.byId[id])
                .filter((data) => {
                    const time = new Date(data?.updated_at || 0).getTime();

                    if (
                        time >= activeLog.attributes.firstDayOfWeek &&
                        time <= activeLog.attributes.lastDayOfWeek
                    )
                        return true;
                    return false;
                })
        ) || [];

    const sum = graphData.reduce(
        (a, b) => a + parseInt(b?.correct_answer || '0'),
        0
    );

    const averageGoal =
        sum / graphData.length || activeLog.attributes.initialWeekGoal || 0;

    function storeLogbook(newEntry?: boolean) {
        const logbookCopy = logbook?.map((e) => {
            if (activeLog.key === e.key) {
                return activeLog;
            }
            return e;
        });

        if (newEntry) {
            const newEntryData = logbookCopy?.length
                ? [...logbookCopy, initialData]
                : [activeLog, initialData];

            dispatch(updateUser({ logbook: newEntryData }));
            setCanSave(false);
            setActiveLog(initialData);
            return;
        }

        const data = logbook?.length ? logbookCopy : [activeLog];

        dispatch(updateUser({ logbook: data }));
    }

    const previousValue = usePrevious(selected);

    useEffect(() => {
        if (previousValue === 'calendar' && canSave) {
            storeLogbook();
            setCanSave(false);
        }
    }, [selected]);

    return (
        <CalendarContext.Provider
            value={{
                storeLogbook,
                canSave,
                setCanSave,
                activeLog,
                setActiveLog,
            }}
        >
            <div styleName="wrapper" ref={ref}>
                <button
                    onClick={(e) => {
                        select('calendar');
                        setMenuPointer(e);
                    }}
                    type="button"
                >
                    <img
                        alt="city icon"
                        src={`/images/menu/icon-calendar${
                            selected === 'calendar' ? '-active' : ''
                        }.svg`}
                    />
                </button>
                {selected === 'calendar' && (
                    <Popup>
                        <div styleName="popup">
                            <div styleName="top">
                                {view === 'Voortgang' ? (
                                    <div>
                                        <h6>Voortgang flitsprogramma</h6>
                                        <p styleName="description">
                                            Aantal woorden per minuut.
                                        </p>
                                    </div>
                                ) : (
                                    <h6>{view}</h6>
                                )}

                                <ToggleButton
                                    onClick={() =>
                                        view === 'Jouw oefenweek'
                                            ? setView('Voortgang')
                                            : setView('Jouw oefenweek')
                                    }
                                    value1="Jouw oefenweek"
                                    value2="Voortgang"
                                />
                            </div>
                            <div styleName="bottom">
                                <div styleName="left">
                                    <DatePicker
                                        logs={
                                            !filteredLogs?.length
                                                ? [initialData]
                                                : filteredLogs
                                        }
                                    />

                                    {view === 'Jouw oefenweek' && <Password />}
                                </div>
                                {view === 'Jouw oefenweek' &&
                                    logbookAuthenticated && (
                                        <div styleName="button-wrapper">
                                            <Button
                                                lightgrey
                                                onClick={() =>
                                                    storeLogbook(true)
                                                }
                                            >
                                                Nieuwe week
                                            </Button>
                                            <Button
                                                lightgrey
                                                onClick={() =>
                                                    setActiveLogForDeleteModal(
                                                        activeLog
                                                    )
                                                }
                                            >
                                                Deze week verwijderen
                                            </Button>
                                        </div>
                                    )}
                            </div>
                            {view === 'Jouw oefenweek' ? (
                                <>
                                    <Days />
                                    <LogBook />
                                </>
                            ) : graphData ? (
                                <Graph
                                    weekGoal={averageGoal}
                                    data={graphData}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                    </Popup>
                )}
            </div>
        </CalendarContext.Provider>
    );
}
